/* eslint-disable consistent-return */
// see comments in useEffect below

import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import 'lazysizes';
import objectFitImages from 'object-fit-images';
import { clientLogger } from '../../../lib/logger';

const getQueryParams = url => {
  try {
    return url.split('?')[1].split('&');
  } catch (e) {
    return [];
  }
};

const parseImageDimensions = img => {
  const dimensions = {};
  try {
    ['small', 'medium'].forEach(size => {
      const parsedQueryParams = getQueryParams(img[size]);
      parsedQueryParams.forEach(param => {
        const [k, v] = param.split('=');
        if (k === 'intrinsicWidth') {
          dimensions.width = `${v}px`;
        } else if (k === 'intrinsicHeight') {
          dimensions.height = `${v}px`;
        }
      });
    });
  } catch (e) {
    clientLogger.warn(`Error parsing image dimensions, Error : ${e}`);
  }
  return dimensions;
};

const initParsedImage = imageUrl => {
  let parsedImage = JSON.parse(imageUrl);
  if (Array.isArray(parsedImage) && parsedImage.length) {
    parsedImage = parsedImage[0];
  }
  if (!parsedImage.alt) {
    parsedImage.alt = '';
  }
  return parsedImage;
};

const buildParsedImage = imageUrl => {
  let parsedImage;
  // This is because not all of PS is updated yet
  try {
    parsedImage = initParsedImage(imageUrl);
    ['small', 'medium'].forEach((size, i) => {
      const imageUri = parsedImage[size];
      if (imageUri) {
        // Default width requested is 320 for small screens and 640 for medium.
        // If width is passed as a param from PS, we override the width/imwidth props with this value.
        let width = (i + 1) * 320;
        const queryParams = getQueryParams(imageUri);
        queryParams.forEach(param => {
          const [k, v] = param.split('=');
          const parsedVal = parseInt(v, 10);
          // The requested width MUST be a multiple of 320
          if (k === 'width' && !isNaN(parsedVal) && parsedVal % 320 === 0) {
            width = v;
          } else if (k === 'sharpen' && v === 'true') {
            parsedImage.sharpen = true;
          }
        });
        parsedImage[size] = `${imageUri}${
          imageUri.includes('?') ? '&' : '?'
        }width=${width}&imwidth=${width}`;
      }
    });
  } catch (e) {
    parsedImage = {
      small: `${imageUrl}?width=320&imwidth=320`,
      medium: `${imageUrl}?width=640&imwidth=640`,
      alt: ''
    };
  }

  return parsedImage;
};

const OptimizedImage = props => {
  const imageRef = useRef(null);
  const { className, imageUrl, disableOptimization = false } = props;
  const parsedImage = buildParsedImage(imageUrl);
  const [dimensions, setDimensions] = useState(
    parseImageDimensions(parsedImage)
  );

  const handleImageSize = () => {
    const windowWidth = window.innerWidth;
    if (windowWidth > 650 && windowWidth < 992) {
      setDimensions({
        ...dimensions,
        width: windowWidth * 0.5
      });
    } else {
      setDimensions(parseImageDimensions(parsedImage));
    }
  };

  useEffect(() => {
    const element = imageRef.current;
    objectFitImages(element);

    if (disableOptimization) {
      return;
    }

    window.addEventListener('resize', handleImageSize);
    return () => window.removeEventListener('resize', handleImageSize);
  }, []);

  return (
    <div className="details-option-image-wrapper" style={dimensions}>
      <div className="details-option-image-inner">
        <picture>
          <source data-srcset={parsedImage.medium} media="(min-width: 768px)" />
          <source
            data-srcset={`${parsedImage.small} 0.5x`}
            media="(min-width: 480px)"
          />
          <source
            data-srcset={`${parsedImage.small} 0.5x`}
            media="(min-width: 0px)"
          />
          <img
            ref={imageRef}
            data-src={parsedImage.small}
            className={`${className} lazyload ${
              parsedImage.sharpen ? 'details-option-contrast' : ''
            }`}
            alt={parsedImage.alt}
          />
        </picture>
      </div>
    </div>
  );
};

OptimizedImage.propTypes = {
  className: PropTypes.string,
  disableOptimization: PropTypes.bool, // don't resize - important for AppsBanner
  imageUrl: PropTypes.string,
  title: PropTypes.string
};
export default OptimizedImage;
